import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar"; // Adjust path based on your folder structure
import { motion } from "framer-motion";
import { db } from "../firebase/firebase";
import { collectionGroup, onSnapshot, query, orderBy } from "firebase/firestore";
import {
  Clock3, Calendar as CalendarIcon, Users, 
  BarChart3, Activity, ShieldCheck, Search, Filter, Globe
} from "lucide-react";

export default function LoginReport() {
  const [logins, setLogins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  // --- 1. FETCH LOGIN RECORDS FROM ALL STORE SUBCOLLECTIONS ---
  useEffect(() => {
    // collectionGroup searches across ALL subcollections named "loginreport"
    const q = query(
      collectionGroup(db, "loginreport"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loginData = snapshot.docs.map(doc => ({
        id: doc.id,
        // Grab the parent store ID from the document reference path
        storeId: doc.ref.parent.parent?.id || "Unknown",
        ...doc.data()
      }));
      setLogins(loginData);
    }, (error) => {
      console.error("Error fetching login reports. Note: You may need to create a composite index in Firebase. Check console for the link.", error);
    });

    return () => unsubscribe();
  }, []);

  // --- 2. FILTER ENGINE (Search, Month, Year) ---
  const filteredLogins = useMemo(() => {
    return logins.filter(record => {
      const matchesSearch = record.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            record.storeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            record.ipAddress?.includes(searchTerm);
                            
      // Using the exact numeric month and year strings saved during login
      const matchesMonth = monthFilter ? record.month === monthFilter : true;
      const matchesYear = yearFilter ? record.year === yearFilter : true;

      return matchesSearch && matchesMonth && matchesYear;
    });
  }, [logins, searchTerm, monthFilter, yearFilter]);

  // --- 3. PIE CHART DATA ENGINE ---
  const pieChartData = useMemo(() => {
    const storeCounts = {};
    filteredLogins.forEach(log => {
      const name = log.storeName || "Unknown Store";
      storeCounts[name] = (storeCounts[name] || 0) + 1;
    });

    const totalLogins = filteredLogins.length;
    const colors = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ec4899", "#06b6d4", "#f43f5e", "#14b8a6"];
    
    let cumulativePercent = 0;
    const data = Object.entries(storeCounts).map(([storeName, count], index) => {
      const percentage = (count / totalLogins) * 100;
      const start = cumulativePercent;
      cumulativePercent += percentage;
      return {
        storeName,
        count,
        percentage: percentage.toFixed(1),
        startPercent: start,
        endPercent: cumulativePercent,
        color: colors[index % colors.length]
      };
    });

    const gradientString = data.map(
      d => `${d.color} ${d.startPercent}% ${d.endPercent}%`
    ).join(", ");

    return { data, gradientString, totalLogins };
  }, [filteredLogins]);

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased">
      
      {/* SUPER ADMIN SIDEBAR */}
      <Sidebar />

      {/* MAIN VIEWPORT */}
      <div className="flex-1 p-6 lg:p-10 space-y-6 overflow-x-hidden">
        
        {/* HEADER BLOCK */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-4 shadow-sm"
        >
          <div>
            <h1 className="text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <ShieldCheck size={26} className="text-blue-600" /> System Login Analytics
            </h1>
            <p className="text-sm text-slate-500 font-medium mt-1">
              Monitor authentication logs, session times, and IP tracking across all tenants.
            </p>
          </div>
        </motion.div>

        {/* FILTER DESK */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Report Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
              <Search size={14} className="text-slate-400"/>
              <input 
                type="text" 
                placeholder="Search Store Name, ID, or IP..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)} 
                className="bg-transparent outline-none w-full text-slate-800 placeholder:font-normal" 
              />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
              <CalendarIcon size={14} className="text-slate-400"/>
              <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="bg-transparent outline-none w-full cursor-pointer">
                <option value="">All Months</option>
                {Array.from({length: 12}, (_, i) => {
                  const numericMonth = String(i + 1).padStart(2, '0');
                  const nameMonth = new Date(0, i).toLocaleString('default', { month: 'long' });
                  return <option key={numericMonth} value={numericMonth}>{nameMonth}</option>
                })}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
              <CalendarIcon size={14} className="text-slate-400"/>
              <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} className="bg-transparent outline-none w-full cursor-pointer">
                <option value="">All Years</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </div>
          </div>
        </div>

        {/* DASHBOARD WIDGETS (PIE CHART & STATS) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* CSS CONIC GRADIENT PIE CHART */}
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col items-center justify-center relative">
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-6 w-full justify-start">
              <BarChart3 size={18} className="text-blue-500" /> Login Distribution
            </h3>
            
            {pieChartData.totalLogins > 0 ? (
              <>
                <div 
                  className="w-48 h-48 rounded-full shadow-inner mb-6 transition-all duration-500 border-4 border-slate-50"
                  style={{ background: `conic-gradient(${pieChartData.gradientString})` }}
                />
                
                <div className="w-full max-h-32 overflow-y-auto space-y-2 pr-2 text-xs font-bold">
                  {pieChartData.data.map(store => (
                    <div key={store.storeName} className="flex justify-between items-center">
                      <div className="flex items-center gap-2 truncate">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: store.color }}></span>
                        <span className="truncate text-slate-700">{store.storeName}</span>
                      </div>
                      <span className="text-slate-500 ml-2">{store.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
               <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                 <Activity size={32} className="mb-2 opacity-50" />
                 <p className="text-xs font-bold uppercase tracking-wider">No Data Available</p>
               </div>
            )}
          </div>

          {/* SUMMARY CARDS */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <motion.div whileHover={{ scale: 1.02 }} className="bg-blue-50 border border-blue-200 rounded-3xl p-6 flex flex-col justify-center relative overflow-hidden group shadow-sm">
              <div className="flex justify-between items-start font-bold">
                <span className="text-xs uppercase tracking-wider text-blue-600 opacity-80">Total Platform Logins</span>
                <div className="p-2 rounded-xl bg-blue-100 text-blue-600"><Users size={18} /></div>
              </div>
              <h2 className="text-5xl font-mono font-black tracking-tight text-blue-700 mt-4">{pieChartData.totalLogins}</h2>
            </motion.div>

            <motion.div whileHover={{ scale: 1.02 }} className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6 flex flex-col justify-center relative overflow-hidden group shadow-sm">
              <div className="flex justify-between items-start font-bold">
                <span className="text-xs uppercase tracking-wider text-emerald-600 opacity-80">Unique Active Stores</span>
                <div className="p-2 rounded-xl bg-emerald-100 text-emerald-600"><Activity size={18} /></div>
              </div>
              <h2 className="text-5xl font-mono font-black tracking-tight text-emerald-700 mt-4">
                {Object.keys(pieChartData.data).length}
              </h2>
            </motion.div>
          </div>
        </div>

        {/* LOGIN HISTORY LEDGER (TABLE) */}
        <div className="space-y-4 mt-8">
          <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <Clock3 size={18} className="text-blue-500" /> Comprehensive Audit Ledger
          </h2>
          
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto text-[11px] font-medium">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider">
                    <th className="p-4 pl-6">Store Identifier</th>
                    <th className="p-4">Network IP Address</th>
                    <th className="p-4">Date Date</th>
                    <th className="p-4 text-right">Exact Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredLogins.length === 0 ? (
                    <tr><td colSpan="4" className="p-8 text-center text-slate-500 font-medium">No login records match current filters.</td></tr>
                  ) : (
                    filteredLogins.map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50/80 transition-colors">
                        
                        <td className="p-4 pl-6">
                          <div className="font-bold text-slate-800 text-xs">{record.storeName || "Unknown"}</div>
                          <div className="font-mono text-blue-600 text-[10px] mt-0.5 tracking-wider">{record.storeId}</div>
                        </td>
                        
                        <td className="p-4">
                          <div className="flex items-center gap-2 font-mono font-bold text-slate-600 bg-slate-100/50 px-2 py-1 rounded-lg w-max">
                            <Globe size={12} className="text-emerald-500" />
                            {record.ipAddress || "Unknown IP"}
                          </div>
                        </td>
                        
                        <td className="p-4">
                          <span className="font-bold text-slate-700">
                            {record.day} {record.monthName}, {record.year}
                          </span>
                        </td>
                        
                        <td className="p-4 text-right font-mono font-black text-xs text-slate-800 bg-slate-50/50">
                          {record.fullTime || "Time N/A"}
                        </td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}